/**
 * ─────────────────────────────────────────────
 * src/lib/taskHours.js
 * ─────────────────────────────────────────────
 * Shared "estimated hours" cap used everywhere a task's start/end date and
 * estimated hours are entered (Create Project's task step, Kanban's Add/Edit
 * Task modals, Projects' task creation) — max 8 hours per working day
 * spanned by the task, inclusive of both the start and end date.
 *
 * Non-working days, excluded from the count:
 *   • every Sunday
 *   • the 1st and 3rd Saturday of every month
 *
 * Example: start = 16th, end = 17th, neither a non-working day → 2 working
 * days → 16 hours max. If one of those two days is a Sunday or a 1st/3rd
 * Saturday, only 1 working day counts → 8 hours max.
 */

const MAX_HOURS_PER_DAY = 8;

/**
 * True for the 1st and 3rd Saturday of the month.
 *
 * Derived from the date rather than a fixed calendar: Saturdays fall on days
 * 1-7 (1st), 8-14 (2nd), 15-21 (3rd), 22-28 (4th), 29-31 (5th), so the
 * occurrence index is ceil(dayOfMonth / 7). Works for every month and year
 * without a lookup table.
 *
 * Takes UTC parts because the caller iterates in UTC.
 */
const isNonWorkingSaturday = (date) => {
  if (date.getUTCDay() !== 6) return false; // 6 = Saturday
  const occurrence = Math.ceil(date.getUTCDate() / 7);
  return occurrence === 1 || occurrence === 3;
};

// Number of working days between two dates, inclusive of both ends, with
// Sundays and 1st/3rd Saturdays excluded. Falls back to 1 day if the dates
// are invalid, missing, or the whole range is non-working days.
export const getInclusiveDayCount = (startDate, endDate) => {
  if (!startDate || !endDate) return 1;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 1;

  // Compare by calendar day only, ignoring time-of-day.
  const startDay = Date.UTC(
    start.getFullYear(),
    start.getMonth(),
    start.getDate(),
  );
  const endDay = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());

  const totalDays = Math.round((endDay - startDay) / (1000 * 60 * 60 * 24)) + 1;
  if (totalDays <= 0) return 1;

  let workingDays = 0;
  for (let i = 0; i < totalDays; i++) {
    const day = new Date(startDay + i * 24 * 60 * 60 * 1000);
    if (day.getUTCDay() === 0) continue; // 0 = Sunday
    if (isNonWorkingSaturday(day)) continue; // 1st + 3rd Saturday
    workingDays += 1;
  }

  return workingDays > 0 ? workingDays : 1;
};

export const getMaxEstimatedHours = (startDate, endDate) =>
  getInclusiveDayCount(startDate, endDate) * MAX_HOURS_PER_DAY;
